import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next && next.startsWith("/") ? next : (u && u.role && !["admin", "recruiting_hr"].includes(u.role)) ? "/ops" : "/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[80vh] flex items-center justify-center py-16 px-4 gradient-radial grain-overlay relative">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 backdrop-blur p-8">
        <img src="/logo.png" alt="Integrity Delivered" className="h-16 w-auto mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-500 flex items-center justify-center"><Lock className="w-5 h-5 text-slate-950" /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Restricted Access</div>
            <h1 className="text-2xl font-extrabold uppercase display text-white">Admin Portal</h1>
          </div>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          {new URLSearchParams(window.location.search).get("expired") && <div data-testid="admin-login-expired" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">Your session expired for security. Sign in again to continue where you left off.</div>}
          <div>
            <Label>Email</Label>
            <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
          </div>
          <Button data-testid="admin-login-submit-btn" type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Sign In
          </Button>
          <div className="text-right">
            <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link" className="text-xs text-slate-400 hover:text-amber-400">Forgot password?</Link>
          </div>
        </form>
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5" /> Authorized personnel only. All access is logged.
        </div>
      </div>
    </section>
  );
}
